import { Empty, Flex, Form, Input, Modal, Select, Table, Tabs, TabsProps, Typography } from "antd"
import { Visit } from "../../domain/jobs/jobs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faUser } from "@fortawesome/pro-solid-svg-icons"
import { roundToPrecision } from "../../helpers/math"
import dayjs from 'dayjs'
import PopupMap from "../maps/PopupMap"
import { useVisitsGpsDataQuery } from "../../domain/jobs/jobsQueries"
import { useEffect, useState } from "react"
import VisitPhotos from "../ScheduleManagement/VisitPhotos"

const TextArea = Input.TextArea

type Props = {
  isOpen: boolean
  visit: Visit
  onClose: (detailsVisit: Visit) => void
  activeTab: string
  googleApiKey: string
  showPhotos?: boolean
  jobId?: number
}

const VisitDetailsModal = ({ isOpen, visit, onClose, activeTab, googleApiKey, showPhotos = false, jobId }: Props) => {
  const [modaltab, setModalTab] = useState(activeTab ?? '1')
  const visitGpsDataQuery = useVisitsGpsDataQuery(visit?.id)
  const gpsData = visitGpsDataQuery.data ?? []
  const [updatedVisit, setUpdatedVisit] = useState(visit)

  const handleTabChange = (value: string) => {
    setModalTab(value)
  }
  
  const statusOptions = [
    { label: 'Current', value: 0 },
    { label: 'Closed', value: 1 },
    { label: 'Submitted', value: 2 },
    { label: 'Reviewed', value: 3 },
  ]
  
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Timesheets',
      children: <div>
        <Form.Item label='Visit Status'>
          <Select 
            options={statusOptions}
            placeholder='Select Status'
            value={updatedVisit?.status}
            onChange={(value) => {
              setUpdatedVisit({...updatedVisit, status: value})
            }}
            disabled={visit?.billed}
            style={{marginBottom: '10px'}}
          />
          <Table 
            dataSource={visit?.timesheets}
            rowKey='timesheetId'
            pagination={{
              pageSize: 5,
            }}
            columns={[
              {
                title: 'Technician',
                render: (timesheet) => <Flex>
                    {timesheet.technician}
                    {timesheet.technician.indexOf(visit?.owner) === 0 && <FontAwesomeIcon icon={faUser} />}
                  </Flex>
              },
              {
                title: 'Start Date',
                render: (timesheet) => {
                  const visitGpsData = gpsData.length > 0 ? gpsData.find(x => x.timesheetId === timesheet?.timesheetId) : null
                  if(visitGpsData?.clockIn) {
                    return <PopupMap
                        apiKey={googleApiKey}
                        center={visitGpsData.clockIn}
                        zoom={15}
                        width={400}
                        height={300}
                        markers={[{ position: visitGpsData.clockIn }]}
                        label={dayjs(timesheet?.startDate).format('MMMM DD, YYYY hh:mm A')}
                      />
                  }

                    return dayjs(timesheet?.startDate).format('MMMM DD, YYYY hh:mm A')
                }

              },
              {
                title: 'End Date',
                render: (timesheet) => {
                  const visitGpsData = gpsData.length > 0 ? gpsData.find(x => x.timesheetId === timesheet?.timesheetId) : null
                  if(visitGpsData?.clockOut) {
                    return <PopupMap
                        apiKey={googleApiKey}
                        center={visitGpsData.clockOut}
                        zoom={15}
                        width={400}
                        height={300}
                        markers={[{ position: visitGpsData.clockOut }]}
                        label={dayjs(timesheet?.endDate).format('MMMM DD, YYYY hh:mm A')}
                      />
                  }

                    return dayjs(timesheet?.endDate).format('MMMM DD, YYYY hh:mm A')
                }
              },
              {
                title: 'QuickBooks Time Exported',
                render: (timesheet) => timesheet.billed ? <FontAwesomeIcon icon={faCheck} /> : ''
                
              },
              {
                title: 'Total Time',
                render: (timesheet) => <Flex>
                  {roundToPrecision(timesheet.time, 2) + ' hr'}
                </Flex>
              }
            ]}
          />
        </Form.Item>
      </div>
    },
    {
      key: '2',
      label: 'Client Notes',
      children: <div>
        <TextArea 
          value={updatedVisit.clientNotes}
          onChange={(e) => setUpdatedVisit({...updatedVisit, clientNotes: e.target.value})}
          style={{height: '200px'}}
        />
      </div>,
    },
    {
      key: '3',
      label: 'Internal Notes',
      children: <div>
        <TextArea 
          value={updatedVisit.internalNotes}
          onChange={(e) => setUpdatedVisit({...updatedVisit, internalNotes: e.target.value})}
          style={{height: '200px'}}
        />
      </div>,
    },
    ...(showPhotos && visit?.id ? [{
      key: '4',
      label: 'Photos',
      destroyOnHidden: true,
      children: <VisitPhotos
        visitId={visit.id}
        jobId={jobId}
        emptyState={<Empty description="No photos for this visit" />}
        errorState={<Typography.Text type="danger">There was a problem loading the photos for this visit.</Typography.Text>}
      />,
    }] : []),
  ];

  useEffect(() => {
    setUpdatedVisit(visit)
  }, [visit])

  useEffect(() => {
    if (isOpen) {
      setModalTab(activeTab)
    }
  }, [isOpen, activeTab])

  return <Modal
    open={isOpen}
    title="Visit Details"
    onCancel={() => onClose(updatedVisit)}
    onOk={() => onClose(updatedVisit)}
    width={900}
  >
    <Tabs
      activeKey={modaltab}
      items={items}
      onChange={handleTabChange}
    />
  </Modal>
}

export default VisitDetailsModal